import { useState } from "react";
import { toast } from "sonner";
import { Mic, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { api, errText } from "@/lib/api";

/** One mic for the whole diary — name the matter as you speak and it files itself. */
export const DictateAnywhereDialog = ({ open, onOpenChange, onSaved }) => {
  const [busy, setBusy] = useState(false);

  const send = async (file) => {
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/logs/dictate", body);
      onOpenChange(false);
      onSaved?.(data);
      toast.success(
        data?.pending_voice ? "Recording saved on this device" : "Recording filed on the diary",
        {
          description: data?.pending_voice
            ? "It will be written up and matched to the hearing you named once you're back online."
            : "It is being written up and matched to the hearing you named — you'll see it on the dashboard.",
          duration: 9000,
        },
      );
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl max-h-[75vh] overflow-y-auto" data-testid="dictate-anywhere-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Dictate a note</DialogTitle>
          <DialogDescription className="text-sm">
            Name the matter as you speak — "in Vertex Infra, the court granted status quo…" — and the
            note is filed on that hearing. Say the next date too and it goes into the diary.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <VoiceRecorder onAudio={send} busy={busy} />
          {busy ? (
            <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4 animate-pulse" /> Filing it…
            </p>
          ) : (
            <p className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Mic className="h-3.5 w-3.5" /> If the matter can't be worked out, the note still lands
              on your dashboard for you to link in one tap.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
